(() => {
  'use strict';

  const FORM_SELECTOR = '#bss-b2b-register-form';
  const COMPANY_SELECTOR = '#company-field';
  const KNOWN_SOURCE_SELECTOR = [
    '#text-field-e3daeabd6fd22',
    '#vat-validation',
    'input[name="vat-validation"]'
  ].join(', ');
  const pendingSyncs = new WeakSet();

  const isVisible = (input) => {
    if (!(input instanceof HTMLInputElement)) return false;

    const field = input.closest('.bss-b2b-rf-field');
    if (!field) return false;

    // BSS adds this attribute to fields hidden by conditional logic.
    if (
      field.hasAttribute('bss-b2b-rf-conditional-logic-hide') ||
      field.classList.contains('bss-hidden') ||
      field.hidden
    ) {
      return false;
    }

    const fieldStyles = window.getComputedStyle(field);
    return fieldStyles.display !== 'none' && fieldStyles.visibility !== 'hidden';
  };

  const getSourceInputs = (form) => {
    const inputs = new Set(form.querySelectorAll(KNOWN_SOURCE_SELECTOR));

    // The NIP field created by BSS has a generated ID, so keep a label-based
    // fallback in case the form configuration changes in the future.
    form.querySelectorAll('label.label-field[for]').forEach((label) => {
      if (!/\b(?:NIP|VAT)\b/i.test(label.textContent)) return;

      const input = document.getElementById(label.htmlFor);
      if (input instanceof HTMLInputElement && form.contains(input)) {
        inputs.add(input);
      }
    });

    return [...inputs];
  };

  const getActiveSourceInput = (form) => {
    return getSourceInputs(form).find(isVisible) || null;
  };

  const syncCompanyField = (form) => {
    const companyInput = form.querySelector(COMPANY_SELECTOR);
    const sourceInput = getActiveSourceInput(form);

    // Do not touch Company while BSS is temporarily re-rendering the fields.
    if (!(companyInput instanceof HTMLInputElement) || !sourceInput) return;

    const sourceValue = sourceInput.value;
    if (companyInput.value === sourceValue) return;

    companyInput.value = sourceValue;

    // Let BSS register the programmatic value just like a value typed by a user.
    companyInput.dispatchEvent(new Event('input', { bubbles: true }));
    companyInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const scheduleSync = (form) => {
    if (pendingSyncs.has(form)) return;

    pendingSyncs.add(form);
    queueMicrotask(() => {
      pendingSyncs.delete(form);
      syncCompanyField(form);
    });
  };

  const isSourceInput = (input, form) => {
    return input instanceof HTMLInputElement && getSourceInputs(form).includes(input);
  };

  const setupForm = (form) => {
    if (form.dataset.companyFieldSyncInit === 'true') return;

    form.dataset.companyFieldSyncInit = 'true';

    form.addEventListener('input', (event) => {
      if (isSourceInput(event.target, form)) {
        // Run after other input handlers, including the uppercase NIP handler.
        scheduleSync(form);
      }
    });

    form.addEventListener('change', (event) => {
      if (isSourceInput(event.target, form)) {
        scheduleSync(form);
      }
    });

    // Covers browser autofill and a submit that follows an input immediately.
    form.addEventListener('submit', () => {
      syncCompanyField(form);
    }, { capture: true });

    scheduleSync(form);
  };

  const initialise = () => {
    const setupCurrentForm = () => {
      const form = document.querySelector(FORM_SELECTOR);
      if (!form) return;

      setupForm(form);
      scheduleSync(form);
    };

    const observer = new MutationObserver(setupCurrentForm);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'bss-b2b-rf-conditional-logic-hide',
        'class',
        'style',
        'hidden'
      ]
    });

    document.addEventListener('BSS_condition_field_change', setupCurrentForm);
    setupCurrentForm();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise);
  } else {
    initialise();
  }
})();
